





































import { computed, defineComponent, PropType, provide, ref, watch } from "@vue/composition-api";
import { EditorOptions } from "@toast-ui/editor/types/editor";
import { debounce } from "lodash-es";
import { ContentFile } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { ContentSet, InputContentSetUpdate } from "@scrile/api-provider/dist/api/ContentSetProvider";
import useDarkModeController from "@/hooks/useDarkModeController";
import { $t } from "@/lib/i18nHelpers";
import { getMediaThumbnailUrl } from "@/lib/urlHelpers";
import { PostFileData } from "@/types";
import BaseInput from "@/components/BaseInput";
import Divider from "@/components/Divider";
import ModalView from "@/components/ModalView";
import PageTitle from "@/components/PageTitle";
import WysiwygEditor, { Emitter } from "@/components/WysiwygEditor";
import EditPostOptions from "../EditPostOptions";
import PostFileItemPreview from "../PostFileItemPreview";
import SafeForWorkOption from "../SafeForWorkOption";

export default defineComponent({
  name: "CreateRichTextView",
  components: {
    PostFileItemPreview,
    Divider,
    SafeForWorkOption,
    BaseInput,
    WysiwygEditor,
    EditPostOptions,
    ModalView,
    PageTitle,
  },
  props: {
    fileList: { type: Array as PropType<Array<PostFileData>>, required: true },
    postData: { type: Object as PropType<ContentSet>, required: true },
  },
  setup(props, { emit }) {
    const { isDarkMode } = useDarkModeController();
    const isEditing = computed(() => props.postData.published);

    const editorOptions: Partial<EditorOptions> = {
      initialEditType: "wysiwyg",
      height: "100%",
      hideModeSwitch: true,
      frontMatter: true,
      placeholder: $t("Content"),
      theme: isDarkMode.value ? "dark" : "light",
    };

    const validCommon = ref(false);
    const fileLoading = ref(false);
    const manualValidation = ref(() => {});
    const updateCb = ref<(() => Promise<void>)[]>([]);
    const values = ref({
      title: props.postData.title ?? "",
      description: props.postData.description ?? "",
    });

    const previewFile = computed(() => props.fileList.filter(file => file.public)?.[0]);
    const uploading = computed(() => props.fileList.some(f => f.uploading));
    const fileUrls = computed(() =>
      props.fileList
        .filter(file => !file.public)
        .reduce((acc, file) => {
          acc[file.id] = getMediaThumbnailUrl(file, file.url, "720p");
          return acc;
        }, {} as Record<string, string>)
    );

    const uploadImageHandler = (eventEmitter: Emitter) => {
      eventEmitter.removeEventHandler("addImageBlobHook");
      eventEmitter.listen("addImageBlobHook", (blob: Blob | File, callback: Function) => {
        const addImageOkButton = document.querySelector(
          ".toastui-editor-popup.toastui-editor-popup-add-image button.toastui-editor-ok-button"
        );
        if (fileLoading.value) return;

        fileLoading.value = true;
        addImageOkButton?.classList.add("disabled");

        emit("addFiles", [blob], {}, (file?: ContentFile) => {
          fileLoading.value = false;
          addImageOkButton?.classList.remove("disabled");
          if (!file) return;
          callback(getMediaThumbnailUrl(file, file.urlPart, "720p"));
        });
        return false;
      });
    };

    const checkUnusedFiles = () => {
      for (const [id, url] of Object.entries(fileUrls.value)) {
        if (!values.value.description.includes(url)) {
          emit("fileRemove", id);
        }
      }
    };

    const onSetUpdateCb = (cb: () => Promise<void>) => {
      updateCb.value.push(cb);
      emit("onSetUpdateCb", cb);
    };

    const removeCb = (cb: () => Promise<void>) => {
      updateCb.value = updateCb.value.filter(i => i !== cb);
      emit("onSetUpdateCb", cb);
    };

    const onPublish = (cb: Function) => {
      const data: Omit<InputContentSetUpdate, "id"> = {
        title: values.value.title,
        description: values.value.description,
        published: true,
      };
      checkUnusedFiles();
      const update = () => Promise.all(updateCb.value.map(i => i()));
      emit("update", { data, closeOnSave: true, beforeUpdate: update, afterUpdate: cb });
    };

    if (!isEditing.value) {
      watch(
        values,
        debounce(() => {
          const data: Omit<InputContentSetUpdate, "id"> = {
            description: values.value.description,
            title: values.value.title,
          };
          emit("update", { data });
        }, 500),
        { deep: true }
      );
    }

    provide("setUpdateCb", onSetUpdateCb);
    provide("removeCb", removeCb);

    return {
      previewFile,
      uploading,
      onPublish,
      values,
      validCommon,
      editorOptions,
      manualValidation,
      uploadImageHandler,
      onSetUpdateCb,
    };
  },
});
